import type { LucideIcon } from 'lucide-react';
import { Card, CardBody } from './Card';
import { cn } from '@/lib/utils';

interface StatCardProps {
  label: string;
  value: string | number;
  delta?: { value: number; label?: string };
  icon?: LucideIcon;
  tone?: 'blue' | 'green' | 'amber' | 'purple' | 'gray';
}

const TONES: Record<NonNullable<StatCardProps['tone']>, string> = {
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
  green: 'bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-300',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',
  purple: 'bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

export function StatCard({ label, value, delta, icon: Icon, tone = 'blue' }: StatCardProps) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
          {Icon && (
            <span className={cn('w-9 h-9 rounded-lg flex items-center justify-center', TONES[tone])}>
              <Icon className="w-4 h-4" />
            </span>
          )}
        </div>
        <div className="flex items-end gap-2">
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
          {delta && (
            <span
              className={cn(
                'text-xs font-semibold pb-1.5',
                delta.value >= 0 ? 'text-green-600' : 'text-red-600',
              )}
            >
              {delta.value >= 0 ? '+' : ''}{delta.value}% {delta.label}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
